import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import PaymentModal from '../components/PaymentModal';
import SecurePublicationReader from '../components/SecurePublicationReader';
import api from '../services/api';
import { 
  Download, BookOpen, QrCode, Search, ShieldCheck, CheckCircle, 
  Sparkles, Award, Share2, X, Folder, FolderOpen, Lock, Clock, Eye
} from 'lucide-react';

interface PublicationItem {
  _id: string;
  id?: string;
  title: string;
  subtitle?: string;
  authors: string[];
  category: string;
  description: string;
  price: number;
  isPaid: boolean;
  coverImage?: string;
  pdfUrl: string;
  pdfFileName: string;
  fileSizeBytes: number;
  pageCount: number;
  qrCodeUrl?: string;
  downloadCount: number;
  readCount: number;
  allowedUsers?: string[];
  tags?: string[];
  jsonContent?: any;
}

const DEFAULT_CSS_MANUAL: PublicationItem = {
  _id: '6a9ae19e688efc01adbdf0db',
  title: 'CSS Islamic Studies Solution Manual',
  subtitle: 'With Past Solved Papers of Islamic Studies (Years 2017 to 2026 Complete)',
  authors: [
    'Advocate Najeeb Elum (Advocate High Court, Jurist & CSS Pedagogical Consultant)',
    'Advocate Naila Hafeez (Lawyer & Researcher, Constitutional Law & Islamic Jurisprudence)'
  ],
  category: 'CSS Examination & Islamic Jurisprudence',
  description: 'A Comprehensive Academic, Jurisprudence and Sociological Treatise with Authentic Arabic Qur’anic Verses, Prophetic Hadith Citations, Classical Exegesis, Contemporary Policy Analysis, and Solid Footnote Citations for Civil Superior Services (CSS) Aspirants and Legal Practitioners.',
  price: 1500,
  isPaid: true,
  coverImage: '/css_islamic_studies_cover.svg',
  pdfUrl: '/uploads/publications/CSS_Islamic_Studies_Solution_Manual.pdf',
  pdfFileName: 'CSS_Islamic_Studies_Solution_Manual.pdf',
  fileSizeBytes: 598017,
  pageCount: 112,
  qrCodeUrl: '',
  downloadCount: 42,
  readCount: 189,
  tags: ['CSS', 'Islamic Studies', 'Solved Papers 2017-2026', 'Jurisprudence']
};

const DownloadsPage: React.FC = () => {
  const [publications, setPublications] = useState<PublicationItem[]>([DEFAULT_CSS_MANUAL]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<string>('All');
  
  // Payment, Reader, and Title Page Modal States
  const [paymentPub, setPaymentPub] = useState<PublicationItem | null>(null);
  const [readerPub, setReaderPub] = useState<any | null>(null);
  const [qrModalPub, setQrModalPub] = useState<PublicationItem | null>(null);
  const [titlePageModalPub, setTitlePageModalPub] = useState<PublicationItem | null>(null);
  
  const [purchasedPubIds, setPurchasedPubIds] = useState<string[]>([]);
  const [pendingPubIds, setPendingPubIds] = useState<string[]>([]);
  const [pendingPaymentsMap, setPendingPaymentsMap] = useState<Record<string, any>>({});

  const [userPurchases, setUserPurchases] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('elum_purchased_publications');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const folders = [
    { id: 'All', name: '📁 All Downloads', count: 1 },
    { id: 'CSS Examination & Islamic Jurisprudence', name: '📚 CSS Solution Manuals (2017-2026)', count: 1 },
    { id: 'Constitutional & Statutory Law', name: '⚖️ Constitutional & Statutory Law', count: 0 },
    { id: 'Superior Courts Citations', name: '🏛️ Superior Courts Case Law Digests', count: 0 },
    { id: 'Research Articles & Monographs', name: '📑 Legal Research Articles', count: 0 }
  ];

  const fetchUserAccessStatus = async () => {
    try {
      let email = '';
      const userStored = localStorage.getItem('user');
      if (userStored) {
        try {
          const parsed = JSON.parse(userStored);
          if (parsed.email) email = parsed.email;
        } catch (e) {}
      }

      const res = await api.get('/payments/my-status', {
        params: { email: email || undefined }
      });

      if (res.data && res.data.success) {
        const pIds = res.data.purchasedPublicationIds || [];
        const pendIds = res.data.pendingPublicationIds || [];
        setPurchasedPubIds(pIds);
        setPendingPubIds(pendIds);

        const pMap: Record<string, any> = {};
        (res.data.payments || []).forEach((p: any) => {
          if (p.publicationId) pMap[p.publicationId] = p;
        });
        setPendingPaymentsMap(pMap);
      }
    } catch (e) {
      console.warn('Could not fetch user subscription status from backend:', e);
    }
  };

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const res = await api.get('/publications', {
        params: {
          category: selectedFolder !== 'All' ? selectedFolder : undefined,
          search: searchTerm ? searchTerm : undefined
        }
      });
      if (res.data && res.data.publications && res.data.publications.length > 0) {
        setPublications(res.data.publications);
      } else {
        setPublications([DEFAULT_CSS_MANUAL]);
      }
    } catch (err) {
      console.warn('Could not fetch publications from API, using default catalog:', err);
      setPublications([DEFAULT_CSS_MANUAL]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublications();
    fetchUserAccessStatus();
  }, [selectedFolder]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPublications();
  };

  const isPurchased = (pub: PublicationItem) => {
    if (!pub.isPaid || pub.price === 0) return true;
    if (purchasedPubIds.includes(pub._id)) return true;
    if (userPurchases.includes(pub._id)) return true;
    return false;
  };

  const isPendingVerification = (pub: PublicationItem) => {
    return pendingPubIds.includes(pub._id);
  };

  const getCoverImage = (pub: PublicationItem | null | undefined): string => {
    if (!pub) return '/css_islamic_studies_cover.svg';
    if (pub.coverImage && pub.coverImage.startsWith('/')) return pub.coverImage;
    const t = (pub.title || '').toLowerCase();
    if (t.includes('islamic')) return '/css_islamic_studies_cover.svg';
    if (t.includes('pakistan')) return '/css_pakistan_affairs_cover.svg';
    if (t.includes('constitution')) return '/constitution_2026_cover.svg';
    if (t.includes('science')) return '/css_general_science_cover.svg';
    if (t.includes('current affairs')) return '/css_current_affairs_cover.svg';
    if (t.includes('talons') || t.includes('wyrm')) return '/crown_of_talons_cover.svg';
    return '/css_islamic_studies_cover.svg';
  };

  const handleDownloadClick = async (pub: PublicationItem) => {
    if (isPurchased(pub)) {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get(`/publications/${pub._id}/download`, {
          params: { token: token || undefined, purchased: 'true' },
          responseType: 'blob'
        });

        if (res.data && res.data.type === 'application/json') {
          const text = await res.data.text();
          const json = JSON.parse(text);
          alert(json.message || 'Could not download PDF.');
          return;
        }

        const blob = new Blob([res.data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', pub.pdfFileName || `${pub.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        console.error('Download error:', err);
        const downloadUrl = `/api/publications/${pub._id}/download?purchased=true`;
        window.open(downloadUrl, '_blank');
      }
    } else if (isPendingVerification(pub)) {
      const pInfo = pendingPaymentsMap[pub._id];
      alert(
        `Payment Proof Already Submitted!\n\nReference / TRX ID: ${pInfo?.transactionId || 'Pending'}\nAmount: PKR ${pub.price.toLocaleString()}\nTransferred to: NAJEEB ULLAH (+92 333 9389948)\n\nStatus: Awaiting Owner / Superuser confirmation in Admin Ingestion Portal. Once verified, download will automatically unlock.`
      );
    } else {
      setPaymentPub(pub);
    }
  };

  const handleReadOnline = async (pub: PublicationItem) => {
    try {
      const res = await api.get(`/publications/${pub._id}/read`);
      if (res.data && res.data.success) {
        setReaderPub(res.data);
      } else {
        setReaderPub(pub);
      }
    } catch (err) {
      setReaderPub(pub);
    }
  };

  const handlePaymentSuccess = async () => {
    await fetchUserAccessStatus();
    await fetchPublications();
    if (paymentPub) {
      const updated = [...userPurchases, paymentPub._id];
      setUserPurchases(updated);
      localStorage.setItem('elum_purchased_publications', JSON.stringify(updated));
      setPaymentPub(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#090d16',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Header />

      {/* TOP HERO & DIRECTORY BREADCRUMB */}
      <section style={{
        background: 'linear-gradient(180deg, #0f172a 0%, #090d16 100%)',
        borderBottom: '2px solid #ecc94b',
        padding: '2.5rem 1.5rem',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* FOLDER PATH BREADCRUMB */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.85rem',
            color: '#94a3b8',
            marginBottom: '1.25rem',
            fontFamily: 'monospace',
            flexWrap: 'wrap'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#ecc94b', fontWeight: '800' }}>
              <FolderOpen size={16} color="#ecc94b" />
              Downloads Repository
            </span>
            <span>/</span>
            <span style={{ color: '#cbd5e1' }}>publications</span>
            <span>/</span>
            <span style={{
              backgroundColor: 'rgba(236,201,75,0.15)',
              color: '#ecc94b',
              padding: '0.2rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid rgba(236,201,75,0.4)',
              fontWeight: '800'
            }}>
              {selectedFolder === 'All' ? 'all-items' : selectedFolder.toLowerCase().replace(/[^a-z0-9]/g, '-')}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.35rem 0.85rem',
                borderRadius: '20px',
                backgroundColor: 'rgba(236,201,75,0.15)',
                border: '1px solid #ecc94b',
                color: '#ecc94b',
                fontSize: '0.78rem',
                fontWeight: '900',
                letterSpacing: '1px',
                marginBottom: '0.75rem',
                textTransform: 'uppercase'
              }}>
                <Sparkles size={14} />
                DIGITAL REPOSITORY &amp; DOWNLOADS ARCHIVE
                <span style={{
                  backgroundColor: '#ecc94b',
                  color: '#0f172a',
                  fontSize: '0.65rem',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '6px',
                  fontWeight: '900',
                  marginLeft: '0.25rem'
                }}>
                  NEW
                </span>
              </div>

              <h1 style={{
                fontSize: '2.2rem',
                fontWeight: '900',
                color: '#ffffff',
                margin: '0 0 0.5rem 0',
                letterSpacing: '0.5px',
                lineHeight: '1.2'
              }}>
                Legal Publications &amp; CSS Solution Manuals
              </h1>
              
              <p style={{
                fontSize: '0.95rem',
                color: '#cbd5e1',
                maxWidth: '780px',
                lineHeight: '1.5',
                margin: 0
              }}>
                Authored by senior High Court advocates and scholars of jurisprudence. Download complete PDF editions or read online with our DRM-protected reader.
              </p>
            </div>

            {/* QUICK ADMIN INGESTION LINK */}
            <a
              href="/admin/portal?tab=publications"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.65rem 1.15rem',
                borderRadius: '12px',
                backgroundColor: '#1e293b',
                border: '1px solid rgba(236,201,75,0.4)',
                color: '#ecc94b',
                fontWeight: '800',
                fontSize: '0.85rem',
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                alignSelf: 'flex-start'
              }}
            >
              <ShieldCheck size={16} />
              <span>Admin Ingestion Panel</span>
            </a>
          </div>

          {/* SEARCH BOX */}
          <form onSubmit={handleSearchSubmit} style={{
            display: 'flex',
            gap: '0.5rem',
            maxWidth: '650px',
            marginBottom: '1.5rem',
            boxSizing: 'border-box'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search publications by title, author, year, or topic..."
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  borderRadius: '10px',
                  border: '1.5px solid #334155',
                  backgroundColor: '#1e293b',
                  color: 'white',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '10px',
                backgroundColor: '#ecc94b',
                color: '#0f172a',
                fontWeight: '900',
                fontSize: '0.9rem',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}
            >
              Search
            </button>
          </form>

          {/* REPOSITORY FOLDER TABS - COMPLETELY STATIONARY */}
          <div style={{
            display: 'flex',
            gap: '0.6rem',
            flexWrap: 'wrap',
            paddingTop: '0.5rem',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFolder(f.id)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backgroundColor: selectedFolder === f.id ? '#ecc94b' : '#1e293b',
                  color: selectedFolder === f.id ? '#0f172a' : '#cbd5e1',
                  border: selectedFolder === f.id ? '1px solid #ecc94b' : '1px solid #334155',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <span>{f.name}</span>
                {f.count > 0 && (
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.1rem 0.45rem',
                    borderRadius: '10px',
                    backgroundColor: selectedFolder === f.id ? '#0f172a' : '#0f172a',
                    color: selectedFolder === f.id ? '#ecc94b' : '#94a3b8',
                    fontWeight: '900'
                  }}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* MAIN CONTENT AREA: FOLDER CONTENTS */}
      <main style={{
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
        padding: '2rem 1.25rem',
        boxSizing: 'border-box',
        flex: 1
      }}>
        
        {/* REPOSITORY STATUS BAR */}
        <div style={{
          backgroundColor: '#0f172a',
          border: '1px solid rgba(236,201,75,0.3)',
          borderRadius: '16px',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(236,201,75,0.15)',
              border: '1px solid #ecc94b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Folder size={22} color="#ecc94b" />
            </div>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#ffffff' }}>
                Folder: <span style={{ color: '#ecc94b' }}>{selectedFolder === 'All' ? 'All Publications' : selectedFolder}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>
                {publications.length} verified item available • PDF &amp; Structured JSON • High-Resolution QR Access
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.45rem 0.85rem',
            borderRadius: '10px',
            backgroundColor: 'rgba(16,185,129,0.15)',
            border: '1px solid rgba(16,185,129,0.4)',
            color: '#34d399',
            fontSize: '0.8rem',
            fontWeight: '800',
            fontFamily: 'monospace'
          }}>
            <ShieldCheck size={16} />
            <span>DRM Protected Vault • Anti-Print Shield Active</span>
          </div>
        </div>

        {/* PUBLICATIONS LIST */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {publications.map((pub) => {
            const purchased = isPurchased(pub);
            const pending = isPendingVerification(pub);
            const pendingInfo = pendingPaymentsMap[pub._id];

            return (
              <div
                key={pub._id}
                style={{
                  backgroundColor: '#0f172a',
                  border: '2px solid rgba(236,201,75,0.4)',
                  borderRadius: '20px',
                  padding: '2rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2.5rem',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  alignItems: 'flex-start',
                  boxSizing: 'border-box'
                }}
              >
                {/* LEFT COLUMN: BOOK MAIN TITLE PAGE (COVER) — HYPERLINKED FOR PREVIEW & DOWNLOAD */}
                <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  
                  {/* TITLE PAGE HEADER LABEL */}
                  <div style={{
                    fontSize: '0.72rem',
                    fontWeight: '900',
                    color: '#ecc94b',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}>
                    <BookOpen size={14} color="#ecc94b" />
                    <span>Main Title Page (Hyperlinked)</span>
                  </div>

                  {/* BOOK MAIN TITLE PAGE (FRONT COVER THUMBNAIL) - HYPERLINKED */}
                  <a 
                    href={`/api/publications/${pub._id}/download?purchased=true`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadClick(pub);
                    }}
                    style={{
                      width: '100%',
                      borderRadius: '14px',
                      border: '2.5px solid #ecc94b',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.6)',
                      cursor: 'pointer',
                      position: 'relative',
                      backgroundColor: '#070c1b',
                      display: 'block',
                      textDecoration: 'none'
                    }}
                    title="Click Main Title Page to Download Complete PDF"
                  >
                    {/* COVER IMAGE */}
                    <img
                      src={getCoverImage(pub)}
                      alt={pub.title}
                      style={{ width: '100%', display: 'block', height: 'auto' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getCoverImage(pub);
                      }}
                    />

                    {/* CLICKABLE ACTION BANNER DIRECTLY ON COVER (STATIONARY) */}
                    <div style={{
                      backgroundColor: purchased ? '#16a34a' : pending ? '#d97706' : '#ca8a04',
                      color: 'white',
                      textAlign: 'center',
                      padding: '0.65rem 0.5rem',
                      fontSize: '0.76rem',
                      fontWeight: '900',
                      letterSpacing: '0.8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      borderTop: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      {purchased ? (
                        <>
                          <Download size={14} />
                          <span>CLICK TITLE PAGE TO DOWNLOAD PDF</span>
                        </>
                      ) : pending ? (
                        <>
                          <Clock size={14} />
                          <span>PROOF SUBMITTED • PENDING APPROVAL</span>
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          <span>CLICK TITLE PAGE TO SUBSCRIBE &amp; DOWNLOAD</span>
                        </>
                      )}
                    </div>
                  </a>

                  {/* PREVIEW FULL TITLE PAGE BUTTON */}
                  <button
                    type="button"
                    onClick={() => setTitlePageModalPub(pub)}
                    style={{
                      marginTop: '0.65rem',
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '8px',
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      fontWeight: '800',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.35rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    <Eye size={14} color="#ecc94b" />
                    <span>Preview High-Res Title Page</span>
                  </button>

                  {/* SUBSCRIPTION AMOUNT MODULE BELOW COVER */}
                  <div style={{
                    marginTop: '0.75rem',
                    width: '100%',
                    backgroundColor: '#090d16',
                    border: '1.5px solid rgba(236,201,75,0.4)',
                    borderRadius: '14px',
                    padding: '0.9rem',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}>
                    <span style={{
                      display: 'block',
                      fontSize: '0.72rem',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      color: '#94a3b8',
                      marginBottom: '4px'
                    }}>
                      SUBSCRIPTION AMOUNT:
                    </span>
                    <div style={{
                      fontSize: '1.45rem',
                      fontWeight: '900',
                      color: '#ecc94b',
                      lineHeight: '1.1',
                      marginBottom: '4px'
                    }}>
                      PKR {pub.price.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#cbd5e1', display: 'block', marginBottom: '8px' }}>
                      Lifetime Single-Seat Access
                    </span>
                    
                    {purchased ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.78rem',
                        fontWeight: '800',
                        color: '#34d399',
                        backgroundColor: 'rgba(16,185,129,0.15)',
                        padding: '0.3rem 0.7rem',
                        borderRadius: '6px'
                      }}>
                        <CheckCircle size={14} /> Subscribed &amp; Unlocked
                      </div>
                    ) : pending ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        color: '#fde047',
                        backgroundColor: 'rgba(234,179,8,0.15)',
                        padding: '0.45rem 0.6rem',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', fontWeight: '800' }}>
                          <Clock size={13} /> Proof Submitted • Verification Pending
                        </div>
                        <span style={{ fontSize: '0.68rem', color: '#cbd5e1' }}>
                          Awaiting confirmation by Owner (+92 333 9389948)
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        color: '#fde047',
                        backgroundColor: 'rgba(234,179,8,0.15)',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px'
                      }}>
                        <Lock size={12} /> Subscription Required for PDF
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN: PUBLICATION DETAILS & ACTIONS */}
                <div style={{ flex: 1, minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  
                  {/* CATEGORY & ISBN ROW */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.35rem 0.85rem',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(126,34,206,0.25)',
                      border: '1px solid #c084fc',
                      color: '#e9d5ff',
                      fontSize: '0.78rem',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {pub.category}
                    </span>

                    <span style={{
                      padding: '0.35rem 0.75rem',
                      borderRadius: '8px',
                      backgroundColor: '#090d16',
                      border: '1px solid #1e293b',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      color: '#94a3b8'
                    }}>
                      ISBN: 978-969-8924-21-0
                    </span>
                  </div>

                  {/* TITLE & SUBTITLE — HYPERLINKED */}
                  <div>
                    <a
                      href={`/api/publications/${pub._id}/download?purchased=true`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadClick(pub);
                      }}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <h2 style={{
                        fontSize: '1.85rem',
                        fontWeight: '900',
                        color: '#ffffff',
                        margin: '0 0 0.4rem 0',
                        lineHeight: '1.2',
                        cursor: 'pointer'
                      }}>
                        {pub.title}
                      </h2>
                    </a>
                    {pub.subtitle && (
                      <p style={{
                        fontSize: '1.05rem',
                        fontWeight: '700',
                        color: '#ecc94b',
                        margin: 0
                      }}>
                        {pub.subtitle}
                      </p>
                    )}
                  </div>

                  {/* AUTHORS BOX */}
                  {pub.authors && pub.authors.length > 0 && (
                    <div style={{
                      backgroundColor: '#090d16',
                      border: '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '0.85rem 1.15rem'
                    }}>
                      <span style={{
                        display: 'block',
                        fontSize: '0.72rem',
                        fontWeight: '800',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        marginBottom: '6px'
                      }}>
                        Authors &amp; Legal Scholars:
                      </span>
                      {pub.authors.map((author, aIdx) => (
                        <div key={aIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.85rem',
                          color: '#e2e8f0',
                          fontWeight: '600',
                          marginTop: '4px'
                        }}>
                          <Award size={15} color="#ecc94b" />
                          <span>{author}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* DESCRIPTION */}
                  <p style={{
                    fontSize: '0.88rem',
                    color: '#cbd5e1',
                    lineHeight: '1.6',
                    margin: 0,
                    textAlign: 'justify'
                  }}>
                    {pub.description}
                  </p>

                  {/* REPOSITORY FILE METRICS */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '0.75rem',
                    paddingTop: '0.5rem'
                  }}>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>Pages</span>
                      <strong style={{ fontSize: '1.1rem', color: '#ecc94b' }}>{pub.pageCount}</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>File Size</span>
                      <strong style={{ fontSize: '1.1rem', color: '#e2e8f0' }}>{Math.round((pub.fileSizeBytes || 598017) / 1024)} KB</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>Solved Papers</span>
                      <strong style={{ fontSize: '1.1rem', color: '#34d399' }}>2017-2026</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>DRM Mode</span>
                      <strong style={{ fontSize: '0.9rem', color: '#38bdf8' }}>Read-Only Shield</strong>
                    </div>
                  </div>

                  {/* ACTION BUTTONS ROW — COMPLETELY STATIONARY */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.75rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #1e293b'
                  }}>
                    
                    {/* BUTTON 1: DOWNLOAD COMPLETE PDF (OR SUBSCRIBE / PENDING) */}
                    <button
                      onClick={() => handleDownloadClick(pub)}
                      style={{
                        flex: '1 1 200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.85rem 1.5rem',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '900',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: purchased ? '#16a34a' : pending ? '#d97706' : '#ecc94b',
                        color: purchased ? '#ffffff' : pending ? '#ffffff' : '#0f172a',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                      }}
                    >
                      {purchased ? (
                        <>
                          <Download size={18} />
                          <span>Download Complete PDF</span>
                        </>
                      ) : pending ? (
                        <>
                          <Clock size={18} />
                          <span>Awaiting Owner Confirmation</span>
                        </>
                      ) : (
                        <>
                          <Download size={18} />
                          <span>Subscribe &amp; Download (PKR {pub.price.toLocaleString()})</span>
                        </>
                      )}
                    </button>

                    {/* BUTTON 2: READ ONLINE (DRM PROTECTED) */}
                    <button
                      onClick={() => handleReadOnline(pub)}
                      style={{
                        flex: '1 1 170px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.85rem 1.25rem',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        backgroundColor: '#4f46e5',
                        color: 'white',
                        border: '1px solid #6366f1',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                      }}
                    >
                      <BookOpen size={18} color="#ecc94b" />
                      <span>Read Online (DRM)</span>
                    </button>

                    {/* BUTTON 3: PREVIEW TITLE PAGE */}
                    <button
                      onClick={() => setTitlePageModalPub(pub)}
                      style={{
                        padding: '0.85rem 1rem',
                        borderRadius: '12px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        color: '#38bdf8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        fontWeight: '800',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                      title="Inspect High-Res Main Title Page"
                    >
                      <Eye size={18} />
                      <span>Title Page</span>
                    </button>

                    {/* BUTTON 4: QR ACCESS */}
                    <button
                      onClick={() => setQrModalPub(pub)}
                      style={{
                        padding: '0.85rem 1rem',
                        borderRadius: '12px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        color: '#ecc94b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        fontWeight: '800',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                      title="Access via Smartphone QR Code"
                    >
                      <QrCode size={18} color="#ecc94b" />
                      <span>QR Access</span>
                    </button>

                  </div>

                </div>
              </div>
            );
          })}
        </div>

      </main>

      {/* QR CODE MODAL POPUP */}
      {qrModalPub && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100000,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#0f172a',
            border: '2px solid #ecc94b',
            borderRadius: '20px',
            maxWidth: '380px',
            width: '100%',
            padding: '1.75rem',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 50px rgba(0,0,0,0.9)'
          }}>
            <button
              onClick={() => setQrModalPub(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: '#1e293b',
                border: 'none',
                borderRadius: '8px',
                color: '#cbd5e1',
                padding: '0.4rem',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>

            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'rgba(236,201,75,0.2)',
              border: '1px solid #ecc94b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto'
            }}>
              <QrCode size={24} color="#ecc94b" />
            </div>

            <h3 style={{ fontSize: '1.15rem', fontWeight: '900', color: 'white', margin: '0 0 0.5rem 0' }}>
              {qrModalPub.title}
            </h3>
            
            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0 0 1rem 0' }}>
              Scan with any mobile smartphone camera for direct access and mobile reading.
            </p>

            {/* QR CODE DISPLAY */}
            <div style={{
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '12px',
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              marginBottom: '1rem'
            }}>
              <img
                src={qrModalPub.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`https://www.elumlaw.com/downloads?id=${qrModalPub._id}`)}`}
                alt="Publication QR Code"
                style={{ width: '180px', height: '180px', display: 'block' }}
              />
            </div>

            <div style={{
              padding: '0.5rem',
              backgroundColor: '#090d16',
              border: '1px solid #1e293b',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              color: '#94a3b8',
              wordBreak: 'break-all',
              marginBottom: '1rem'
            }}>
              https://www.elumlaw.com/downloads?id={qrModalPub._id}
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(`https://www.elumlaw.com/downloads?id=${qrModalPub._id}`);
                alert('Direct publication link copied to clipboard!');
              }}
              style={{
                width: '100%',
                padding: '0.65rem',
                borderRadius: '10px',
                backgroundColor: '#1e293b',
                color: '#ecc94b',
                border: '1px solid #475569',
                fontWeight: '800',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem'
              }}
            >
              <Share2 size={16} />
              <span>Copy Direct Link</span>
            </button>
          </div>
        </div>
      )}

      {/* FULLSCREEN TITLE PAGE & BIBLIOGRAPHIC DETAILS MODAL */}
      {titlePageModalPub && (
        <div 
          onClick={() => setTitlePageModalPub(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid #ecc94b',
              borderRadius: '20px',
              maxWidth: '850px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '2rem',
              position: 'relative',
              boxShadow: '0 20px 50px rgba(0,0,0,0.95)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem'
            }}
          >
            <button
              onClick={() => setTitlePageModalPub(null)}
              style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                backgroundColor: '#1e293b',
                border: 'none',
                borderRadius: '8px',
                color: '#cbd5e1',
                padding: '0.45rem',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BookOpen size={22} color="#ecc94b" />
              <h3 style={{ fontSize: '1.3rem', fontWeight: '900', color: 'white', margin: 0 }}>
                Authentic Publication Title Page &amp; Frontispiece
              </h3>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
              {/* HIGH RESOLUTION COVER PREVIEW */}
              <div style={{
                width: '320px',
                flexShrink: 0,
                borderRadius: '14px',
                border: '2px solid #ecc94b',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                backgroundColor: '#070c1b'
              }}>
                <img
                  src={getCoverImage(titlePageModalPub)}
                  alt={titlePageModalPub.title}
                  style={{ width: '100%', display: 'block', height: 'auto' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getCoverImage(titlePageModalPub);
                  }}
                />
              </div>

              {/* BIBLIOGRAPHIC DETAILS */}
              <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#ecc94b', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {titlePageModalPub.category}
                  </span>
                  <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: 'white', margin: '0.2rem 0 0.25rem 0', lineHeight: '1.2' }}>
                    {titlePageModalPub.title}
                  </h2>
                  {titlePageModalPub.subtitle && (
                    <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#cbd5e1', margin: 0 }}>
                      {titlePageModalPub.subtitle}
                    </p>
                  )}
                </div>

                <div style={{ backgroundColor: '#1e293b', padding: '0.85rem 1rem', borderRadius: '10px', border: '1px solid #334155' }}>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '800', marginBottom: '4px' }}>
                    Authors &amp; Contributors
                  </span>
                  {titlePageModalPub.authors.map((auth, aIdx) => (
                    <div key={aIdx} style={{ fontSize: '0.85rem', color: '#f8fafc', fontWeight: '600', marginTop: '2px' }}>
                      • {auth}
                    </div>
                  ))}
                </div>

                <div style={{ backgroundColor: '#1e293b', padding: '0.85rem 1rem', borderRadius: '10px', border: '1px solid #334155' }}>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '800', marginBottom: '4px' }}>
                    Cataloging in Publication Data
                  </span>
                  <div style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                    Publisher: <strong>Elum Law Academic &amp; Research Press</strong><br />
                    Jurisdiction: <strong>Islamic Republic of Pakistan</strong><br />
                    ISBN: <strong>978-969-8924-21-0</strong><br />
                    Format: <strong>Full-text PDF &amp; Structured JSON Compendium</strong><br />
                    Total Pages: <strong>{titlePageModalPub.pageCount} Pages</strong>
                  </div>
                </div>

                {/* MODAL ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setTitlePageModalPub(null);
                      handleDownloadClick(titlePageModalPub);
                    }}
                    style={{
                      flex: '1 1 180px',
                      padding: '0.75rem 1.25rem',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: isPurchased(titlePageModalPub) ? '#16a34a' : '#ecc94b',
                      color: isPurchased(titlePageModalPub) ? 'white' : '#0f172a',
                      fontWeight: '900',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <Download size={16} />
                    <span>{isPurchased(titlePageModalPub) ? 'Download Complete PDF' : `Subscribe & Download (PKR ${titlePageModalPub.price.toLocaleString()})`}</span>
                  </button>

                  <button
                    onClick={() => {
                      setTitlePageModalPub(null);
                      handleReadOnline(titlePageModalPub);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '10px',
                      border: '1px solid #6366f1',
                      backgroundColor: '#4f46e5',
                      color: 'white',
                      fontWeight: '800',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    <BookOpen size={16} />
                    <span>Read Online</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT MODAL FOR SUBSCRIPTION */}
      {paymentPub && (
        <PaymentModal
          paymentData={{
            id: paymentPub._id,
            amount: paymentPub.price,
            type: 'publication_subscription'
          }}
          onClose={() => setPaymentPub(null)}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* FULL-SCREEN DRM SECURE READER */}
      {readerPub && (
        <SecurePublicationReader
          publication={readerPub}
          userEmail="reader@elumlaw.com"
          userName="Valued Legal Scholar"
          onClose={() => setReaderPub(null)}
        />
      )}

    </div>
  );
};

export default DownloadsPage;
